/**
 * `/automations` page.
 *
 * The scheduled machinery that runs on a clock and reports to PEOPLE —
 * manager digests, admin health alerts, per-trainer briefings. Split out of
 * `/flows` on 2026-08-05: the two pages answer different questions and were
 * only ever adjacent. `/flows` is about a MEMBER's journey (who is enrolled,
 * where they are in a drip, what a trigger sent them); this is about the
 * studio's own cron jobs (what runs, on what schedule, did the last one
 * work). Reading one to find the other meant scrolling past a table that had
 * nothing to do with the question.
 *
 * READ-ONLY, like the sections it replaces: schedules live in code (the saga
 * registry), not in per-studio config, so there is nothing here to edit yet.
 */

import type { CatalogSagaEntry, SagaRunSummary } from "@layer/dashboard/types";
import { api } from "../lib/api";
import { useCachedResource } from "../lib/resource-cache";
import { studioTimeZone } from "../lib/studio-tz";

interface CatalogSagasResponse {
  readonly sagas?: ReadonlyArray<CatalogSagaEntry>;
}

interface SagasResponse {
  readonly recent_runs: ReadonlyArray<SagaRunSummary>;
}

/**
 * The three non-member audience sections. Each is driven by the
 * audience-tagged cron sagas (`/api/catalog` sagas) + their last run
 * (`/api/sagas` recent_runs). READ-ONLY; editability is a later phase.
 */
const AUDIENCE_SECTIONS: ReadonlyArray<{
  readonly audience: "manager" | "admin" | "trainer";
  readonly heading: string;
  readonly blurb: string;
}> = [
  {
    audience: "manager",
    heading: "Manager automations",
    blurb:
      "Revenue + retention digests sent to managers — read-only here (edit lands in a later phase).",
  },
  {
    audience: "admin",
    heading: "Admin automations",
    blurb:
      "Operational health + failure alerts for admins — read-only here (edit lands in a later phase).",
  },
  {
    audience: "trainer",
    heading: "Trainer automations",
    blurb:
      "Per-trainer briefings — read-only here (edit lands in a later phase).",
  },
];

/**
 * Phase 81 — render a 5-field POSIX cron expression human-readably for
 * the common shapes Layer's cron sagas use. Falls back to the raw
 * expression when the shape isn't one we special-case. No new
 * dependency — a tiny pattern matcher, not a full cron parser.
 *
 * Inngest evaluates cron in UTC by default (Layer's cron sagas gate on
 * studio-local time inside the step, not in the cron), so the wall-clock
 * shapes are labelled "UTC".
 */
function cronToText(cron: string): string {
  const parts = cron.trim().split(/\s+/);
  if (parts.length !== 5) return cron;
  const [min, hour, dom, mon, dow] = parts as [
    string,
    string,
    string,
    string,
    string,
  ];
  const everyField = dom === "*" && mon === "*" && dow === "*";
  // Every-N-minutes: "*/N * * * *"
  const stepMin = /^\*\/(\d+)$/.exec(min);
  if (stepMin && hour === "*" && everyField) {
    const n = Number(stepMin[1]);
    return n === 1 ? "Every minute" : `Every ${n} minutes`;
  }
  // Hourly on the hour: "0 * * * *" / "M * * * *"
  if (/^\d+$/.test(min) && hour === "*" && everyField) {
    const m = Number(min);
    return m === 0 ? "Hourly (on the hour)" : `Hourly (at :${pad2(m)})`;
  }
  // Daily at a fixed time: "M H * * *"
  if (/^\d+$/.test(min) && /^\d+$/.test(hour) && everyField) {
    return `Daily at ${pad2(Number(hour))}:${pad2(Number(min))} UTC`;
  }
  // Every minute: "* * * * *"
  if (min === "*" && hour === "*" && everyField) return "Every minute";
  return cron;
}

function pad2(n: number): string {
  return n < 10 ? `0${n}` : String(n);
}

/**
 * Friendly label for an event-triggered saga's `trigger_event` — these have no
 * cron, so the page would otherwise show "—". E.g. `member.session_completed`
 * → "After each session". Falls back to a de-namespaced phrasing for unmapped
 * events so a future event is never blank.
 */
function eventToText(event: string): string {
  switch (event) {
    case "member.session_completed":
      return "After each session";
    default:
      return `On ${event.replace(/[._]/g, " ")}`;
  }
}

interface ScheduleDisplay {
  /** Human-readable schedule, always present. */
  readonly label: string;
  /** Raw cron to show in a <code> chip; null when it would mislead/not apply. */
  readonly mono: string | null;
}

/**
 * Resolve the truthful schedule for an automation row. Precedence:
 *   1. `local_send_hour` — an hourly cron gated to a studio-local hour. Show
 *      "Daily ~HH:00 (studio local)" and DROP the raw `0 * * * *` (it reads as
 *      "hourly" and is the artifact we're correcting).
 *   2. `trigger_cron` — a literal cron; humanise it + show the raw expression.
 *   3. `trigger_event` — an event-triggered saga; show the friendly event text.
 *   4. neither — "—".
 */
function scheduleDisplay(row: AutomationRow): ScheduleDisplay {
  if (row.local_send_hour !== null) {
    return {
      label: `Daily ~${pad2(row.local_send_hour)}:00 (studio local)`,
      mono: null,
    };
  }
  if (row.trigger_cron !== null) {
    return { label: cronToText(row.trigger_cron), mono: row.trigger_cron };
  }
  if (row.trigger_event !== null) {
    return { label: eventToText(row.trigger_event), mono: null };
  }
  return { label: "—", mono: null };
}

/**
 * Phase 81 — derive a short human label from a saga name when no
 * description metadata exists (Title Case of the snake_case name).
 */
function sagaLabel(name: string): string {
  return name
    .split("_")
    .map((w) => (w.length === 0 ? w : w[0]!.toUpperCase() + w.slice(1)))
    .join(" ");
}

interface AutomationRow {
  readonly name: string;
  readonly trigger_cron: string | null;
  readonly trigger_event: string | null;
  readonly local_send_hour: number | null;
  readonly lastRun: SagaRunSummary | null;
}

/**
 * Phase 81 — match each audience-tagged saga to its most-recent run
 * (by `updated_at`) from the `/api/sagas` recent_runs list. Cron sagas
 * record their run under `saga_class === <saga name>` (falls back to
 * `action`), so we match on either.
 */
function resolveLastRun(
  sagaName: string,
  runs: ReadonlyArray<SagaRunSummary>,
): SagaRunSummary | null {
  let best: SagaRunSummary | null = null;
  for (const r of runs) {
    if (r.saga_class !== sagaName && r.action !== sagaName) continue;
    if (best === null || r.updated_at > best.updated_at) best = r;
  }
  return best;
}

function runStatusLabel(s: SagaRunSummary["status"]): string {
  switch (s) {
    case "pending":
      return "queued";
    case "in_progress":
      return "running";
    case "done":
      return "done";
    case "failed":
      return "failed";
    case "ingress_failed":
      return "ingress-failed";
  }
}

function runStatusClass(s: SagaRunSummary["status"]): string {
  switch (s) {
    case "done":
      return "tag tag-success";
    case "failed":
    case "ingress_failed":
      return "tag tag-danger";
    case "in_progress":
    case "pending":
      return "tag tag-info";
  }
}

/**
 * The column model all three automation tables share.
 *
 * They stack on one page with IDENTICAL headings, so self-sized columns made
 * three tables of the same thing look like three different tables — each one's
 * widths set by its own longest saga name. Fixed layout + one shared colgroup
 * makes the widths a property of the page, so the three read as one list broken
 * into sections, which is what they are.
 */
function AutomationsColGroup(): JSX.Element {
  return (
    <colgroup>
      <col className="autos-col-name" />
      <col className="autos-col-does" />
      <col className="autos-col-schedule" />
      <col className="autos-col-lastrun" />
    </colgroup>
  );
}

export function Automations(): JSX.Element {
  // Same cache keys as `/flows` used, so moving these sections to their own
  // page costs no extra request: whichever page is visited first fills the
  // cache and the other renders from it.
  const { data: catalog, error: catalogErr } =
    useCachedResource<CatalogSagasResponse>("catalog", () =>
      api.get<CatalogSagasResponse>("/api/catalog"),
    );
  const { data: sagasCache } = useCachedResource<SagasResponse>("sagas", () =>
    api.get<SagasResponse>("/api/sagas"),
  );

  const recentRuns = sagasCache?.recent_runs ?? [];

  const automationsByAudience = (() => {
    const buckets: Record<"manager" | "admin" | "trainer", AutomationRow[]> = {
      manager: [],
      admin: [],
      trainer: [],
    };
    for (const s of catalog?.sagas ?? []) {
      // 2026-08-18 — `audience` is a LIST on the wire, and a saga may appear in
      // more than one section. `tasks_morning_digest` is why: it messages every
      // manager with an open task AND every trainer with one, but was tagged
      // "manager" alone, so a daily 07:00 WhatsApp to trainers was missing from
      // the very section that claims to list trainer automations. Listing it
      // twice is correct — both audiences genuinely receive it.
      for (const a of s.audience ?? []) {
        if (a !== "manager" && a !== "admin" && a !== "trainer") continue;
        buckets[a].push({
          name: s.name,
          trigger_cron: s.trigger_cron,
          trigger_event: s.trigger_event,
          local_send_hour: s.local_send_hour,
          lastRun: resolveLastRun(s.name, recentRuns),
        });
      }
    }
    for (const key of ["manager", "admin", "trainer"] as const) {
      buckets[key].sort((a, b) => a.name.localeCompare(b.name));
    }
    return buckets;
  })();

  if (catalogErr !== undefined && catalogErr !== null) {
    return (
      <div>
        <h1 className="page-title">Automations</h1>
        <p className="empty">
          Couldn&apos;t load automations ({catalogErr.message}).
        </p>
      </div>
    );
  }

  if (catalog === undefined) {
    return (
      <div>
        <h1 className="page-title">Automations</h1>
        <p className="empty">Loading automations…</p>
      </div>
    );
  }

  return (
    <div>
      <h1 className="page-title">Automations</h1>
      {/* An empty section still renders its heading + a muted "none yet"
          rather than vanishing — a missing heading reads as a page that
          failed to load, not as an audience with nothing scheduled. */}
      {AUDIENCE_SECTIONS.map((section) => {
        const rows = automationsByAudience[section.audience];
        return (
          <section
            key={section.audience}
            style={{ marginTop: 32 }}
            aria-label={section.heading}
          >
            <h2>{section.heading}</h2>
            <p className="subtle">{section.blurb}</p>
            {rows.length === 0 ? (
              <p className="subtle">none yet</p>
            ) : (
              <table className="data-table autos-table">
                <AutomationsColGroup />
                <thead>
                  <tr>
                    <th>Automation</th>
                    <th>What it does</th>
                    <th>Schedule</th>
                    <th>Last run</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row) => (
                    <tr key={row.name}>
                      <td className="autos-cell-name">
                        <code>{row.name}</code>
                      </td>
                      <td data-label="What it does">{sagaLabel(row.name)}</td>
                      <td data-label="Schedule">
                        {(() => {
                          const sched = scheduleDisplay(row);
                          return sched.label === "—" ? (
                            <span className="subtle">—</span>
                          ) : (
                            <>
                              {sched.label}
                              {sched.mono !== null && (
                                <>
                                  {" "}
                                  <code className="subtle">{sched.mono}</code>
                                </>
                              )}
                            </>
                          );
                        })()}
                      </td>
                      <td data-label="Last run">
                        {row.lastRun === null ? (
                          <span className="subtle">never run</span>
                        ) : (
                          <>
                            <span className={runStatusClass(row.lastRun.status)}>
                              {runStatusLabel(row.lastRun.status)}
                            </span>{" "}
                            <span className="subtle">
                              {new Date(row.lastRun.updated_at).toLocaleString(
                                "en-GB",
                                { timeZone: studioTimeZone() },
                              )}
                            </span>
                          </>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </section>
        );
      })}
    </div>
  );
}
